import groupBy from 'lodash/groupBy';
import { isJSMProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import type { State } from '../types';
import { JSM_PREMIUM_OFFERING_ID, isStatusPageAddOnProduct } from '@css-xp/ppc-utils';
import { stitchChangeKeyObjects } from '../utils';
import type { PresentationLayerProducts, cartLinkedEntitlement } from '../../ui/product-line-items/src/types';
import { getSearchResults } from '../utils/get-search-results';
import { CartLinkedEntitlementsSchema, CartProductSchema, LinkedEntitlementRelationShipType } from '../../types';
import { sortLinkedEntitlements } from '../utils/sort-linked-entitlements';

type StoreProduct = State['products'][keyof State['products']];

/**
 * Receives a list of products and apps as a k-v pair (k = product ID or cloud site ID and v = list of apps)
 * The list can have products belonging to 1 or many containers (like JSM) and some standalone products (like confluence)
 *
 * 1st, we will process the products belonging to 1 or many containers.
 * 1. Group by container ID gives us k-v pair (k = container ID and v = list of products under said container ID)
 * 2. Build container product for presentation layer
 * 3. Add the container product from #2 to results list
 *
 * Now, we will process standalone products.
 * 1. Loop through standalone products and adjust it for presentation
 * 2. Add the adjusted product from #1 to the results list
 *
 * @param cloudSiteProducts is the list of products belonging to a particular cloudsite
 * @param state current state in store
 * @returns PresentationLayerProducts
 */
function getProductsForPresentation(
  cloudSiteProducts: StoreProduct[],
  state: State,
  eligibleAppItemIds?: string[]
): PresentationLayerProducts {
  const linkedEntitlements = state.linkedEntitlements;
  const estimatedUsersForRecurringBillEstimate = state?.estimatedUsersForRecurringBillEstimate;
  const productsForPresentation: PresentationLayerProducts = [];
  const containerProducts = cloudSiteProducts.filter((product) => !!product.containerId);
  const productsGroupedByContainer = groupBy(containerProducts, 'containerId');
  const getAppWithEstimate = (app: cartLinkedEntitlement) => {
    const { listPrice, totalTax, total, totalPartnerDiscount, totalOtherDiscount } =
      state.recurringBillEstimate?.estimates?.[app.id] || {};

    return {
      ...app,
      // this is required to display estimated users in the cart line items not for order placement
      // if we have estimated users for recurring bill estimate, use that, else use the active users from the app
      // https://hello.atlassian.net/wiki/spaces/COMITFO/pages/3403322557/Allow+users+column+to+edit+in+UOF+cart+page
      activeUsers: estimatedUsersForRecurringBillEstimate?.[app.id]
        ? `${estimatedUsersForRecurringBillEstimate?.[app.id]}`
        : `${app.activeUsers}`,
      listPrice,
      total,
      totalTax,
      partnerMargin: totalPartnerDiscount,
      otherDiscount: totalOtherDiscount,
    };
  };

  const getEligibleApps = (linkedEntitlements: CartLinkedEntitlementsSchema[]) => {
    if (!eligibleAppItemIds?.length) return linkedEntitlements;
    return linkedEntitlements?.filter(
      (app) =>
        eligibleAppItemIds.includes(app?.itemId ?? '') ||
        eligibleAppItemIds.includes(app.id) ||
        eligibleAppItemIds.includes(app.productId)
    );
  };

  Object.keys(productsGroupedByContainer).forEach((containerId) => {
    const products = productsGroupedByContainer[containerId];
    const selectedJSMProduct = products.find((product) => isJSMProduct(product.productId));
    const mappedLinkedEntitlements = (() => {
      const containerLinkedEntitlements = getEligibleApps(linkedEntitlements[containerId])?.filter(
        (linkedEntitlement) => {
          // if we don't have a JSM product in selected cart, then we don't show add-ons in the cart, also we should not show add-ons for JSM non premium
          if (
            (!selectedJSMProduct ||
              (!!selectedJSMProduct && selectedJSMProduct?.offeringId !== JSM_PREMIUM_OFFERING_ID)) &&
            linkedEntitlement?.relationshipType === LinkedEntitlementRelationShipType.ADD_ON
          ) {
            return false; // don't show add-ons if we don't have a JSM product in the cart or if the JSM product is not a premium
          }
          return true;
        }
      );
      return (
        containerLinkedEntitlements?.map((containerLinkedEntitlement) =>
          getAppWithEstimate(containerLinkedEntitlement)
        ) || []
      );
    })();
    // sorting linked entitlements by relationshipType and name
    const sortedLinkedEntitlements = sortLinkedEntitlements(mappedLinkedEntitlements);
    const containerProduct: PresentationLayerProducts[number] = {
      type: 'container',
      linkedEntitlements: sortedLinkedEntitlements,
      products: products.map((product) => ({
        ...product,
        // this is required to display estimated estimated users in the cart line items not for order placement
        // if we have estimated users for recurring bill estimate, use that, else use the active users from the product
        // https://hello.atlassian.net/wiki/spaces/COMITFO/pages/3403322557/Allow+users+column+to+edit+in+UOF+cart+page
        activeUsers: estimatedUsersForRecurringBillEstimate?.[product.id]
          ? `${estimatedUsersForRecurringBillEstimate?.[product.id]}`
          : `${product.activeUsers}`,
        listPrice: product.listPrice,
        totalTax: product.totalTax,
        total: product.total,
        partnerMargin: product.partnerMargin,
        otherDiscount: product?.otherDiscount,
      })),
    };

    productsForPresentation.push(containerProduct);
  });

  const standaloneProducts = cloudSiteProducts.filter((product) => !product.containerId);

  standaloneProducts.forEach((product) => {
    const filteredLinkedEntitlements = getEligibleApps(linkedEntitlements[product.id])?.filter((linkedEntitlement) => {
      if (isStatuspage(product?.productId)) {
        // if we have a statuspage product, then we show add-ons only based on the statuspage offeringId
        return state.statuspageAddonsOfferingsMap?.[product?.offeringId]?.find(
          (offering) => offering?.offeringId === linkedEntitlement?.offeringId
        );
      }
      return true;
    });
    const standaloneProduct: PresentationLayerProducts[number] = {
      ...product,
      type: 'standalone',
      // this is required to display estimated estimated users in the cart line items not for order placement
      // if we have estimated users for recurring bill estimate, use that, else use the active users from the product
      // https://hello.atlassian.net/wiki/spaces/COMITFO/pages/3403322557/Allow+users+column+to+edit+in+UOF+cart+page
      activeUsers: estimatedUsersForRecurringBillEstimate?.[product.id]
        ? `${estimatedUsersForRecurringBillEstimate?.[product.id]}`
        : `${product.activeUsers}`,
      listPrice: product.listPrice,
      totalTax: product.totalTax,
      total: product.total,
      partnerMargin: product.partnerMargin,
      otherDiscount: product?.otherDiscount,
      linkedEntitlements: filteredLinkedEntitlements?.map((app) => getAppWithEstimate(app)) || [],
    };

    productsForPresentation.push(standaloneProduct);
  });

  return productsForPresentation;
}

export function getProductsGroupByCloudSiteOrOrgName(
  state: State,
  productsWithListPriceAndMargin: CartProductSchema[],
  eligibleAppItemIds?: string[]
) {
  const cloudsiteProducts = productsWithListPriceAndMargin.filter((product) => !product.organizationId);
  const selectedProductsGroupedByCloudSite = groupBy(cloudsiteProducts, 'cloudSite');
  const selectedCloudSites = Object.keys(selectedProductsGroupedByCloudSite);

  const orgProducts = productsWithListPriceAndMargin.filter((product) => product.organizationId);
  const selectedProductsGroupedByOrgName = groupBy(orgProducts, 'organisationName');
  const selectedOrgProducts = Object.keys(selectedProductsGroupedByOrgName);

  return [
    ...selectedOrgProducts?.map((orgName) => {
      const orgProducts = selectedProductsGroupedByOrgName[orgName];
      const productsForPresentation = getProductsForPresentation(orgProducts, state, eligibleAppItemIds);

      return {
        cloudSite: '',
        cloudSiteId: '',
        products: productsForPresentation,
        orgName,
        orgId: orgProducts[0]?.organizationId ?? '',
        isOrgProduct: orgProducts[0]?.isOrgProduct,
      };
    }),
    ...selectedCloudSites?.map((cloudSite) => {
      const cloudsiteProducts = selectedProductsGroupedByCloudSite[cloudSite];
      const productsForPresentation = getProductsForPresentation(cloudsiteProducts, state, eligibleAppItemIds);

      return {
        cloudSite,
        cloudSiteId: cloudsiteProducts[0]?.cloudSiteId,
        products: productsForPresentation,
        orgName: '',
        orgId: '',
        isOrgProduct: false,
      };
    }),
  ];
}

/**
 * Products are kept in store in a k-v pair: k = product ID and v = product details
 * User selection can happen on either an individual product or a cloudsite as a "select-all" (at which point we will keep the list of product ids in selected list). This will help us when user removes an individual line item from the cart.
 *
 * The cart screen needs content grouped by cloud site.
 */
export function getProductLineItems(state: State) {
  const searchText = state.cartSearch?.query;
  const filterStatuses = state.cartSearch?.statuses;

  const selectedProducts = Object.values(stitchChangeKeyObjects(state.products, state.selectedProducts));

  const productsToRender = (() => {
    if (!searchText && !filterStatuses?.length) return selectedProducts;

    const searchResults = getSearchResults({
      selectedProducts,
      offerings: state.offeringsAndpricingPlans,
      searchText,
      filterStatuses,
    });

    // user has entered a search text that matched no products
    if (searchResults.length === 0) return [];

    return searchResults;
  })();

  return createProductLineItems(state, productsToRender);
}

export function createProductLineItems(
  state: State,
  productsToRender: CartProductSchema[],
  eligibleAppItemIds?: string[]
) {
  const productsWithListPriceAndMargin = productsToRender.map((product) => {
    if (!state.recurringBillEstimate) return product;

    const { listPrice, totalTax, total, totalPartnerDiscount, totalOtherDiscount } =
      state.recurringBillEstimate.estimates?.[product.id] || {};

    return {
      ...product,
      listPrice,
      totalTax,
      total,
      partnerMargin: totalPartnerDiscount,
      otherDiscount: totalOtherDiscount,
    };
  });

  return getProductsGroupByCloudSiteOrOrgName(state, productsWithListPriceAndMargin, eligibleAppItemIds);
}
